import { useState, useCallback } from "react";

/**
 * Generic hook for persisting drill options to localStorage
 * Uses lazy initialization to read from localStorage synchronously on mount
 *
 * @param storageKey - The localStorage key (e.g., "drill_interval_storage")
 * @param defaultOptions - Default options to use if nothing is stored
 * @returns [options, setOptions, isLoaded] tuple like useState, with isLoaded flag
 */
export function useDrillLocalStorage<T>(
    storageKey: string,
    defaultOptions: T
): [T, (options: T | ((prev: T) => T)) => void, boolean] {
    // Lazy initialization: read from localStorage synchronously during state initialization
    const getInitialOptions = (): T => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                return JSON.parse(stored) as T;
            }
        } catch (error) {
            // Silently fail and use defaults
        }
        return defaultOptions;
    };

    const [options, setOptionsState] = useState<T>(getInitialOptions);

    // Save to localStorage when options change
    const setOptions = useCallback((newOptions: T | ((prev: T) => T)) => {
        setOptionsState((prevOptions) => {
            const updatedOptions = typeof newOptions === "function"
                ? (newOptions as (prev: T) => T)(prevOptions)
                : newOptions;

            try {
                localStorage.setItem(storageKey, JSON.stringify(updatedOptions));
            } catch (error) {
                // Silently fail - non-critical feature
            }
            return updatedOptions;
        });
    }, [storageKey]);

    return [options, setOptions, true]; // isLoaded is always true since we load synchronously
}
